import { ReplaceMap } from '../interfaces/replaceMap';
import { ObjHlp } from './object.helper';

import diacriticRegex from './integratedDiacritics.json';

/**
 * Various functions for string manipulation
 */
export class StrHlp {
  /**
   * Checks whether a value is a string containing a non-whitespace character.
   * @param {unknown} value Value to check
   * @return {boolean} Whether the value is a non-empty string
   */
  public static isNonEmptyString(value: unknown): value is string {
    return typeof value === 'string' && !!value.trim();
  }

  /**
   * Checks a bounded identifier without surrounding whitespace or control characters.
   * @param {unknown} value     Value to check
   * @param {number}  maxLength Maximum identifier length
   * @return {boolean} Whether the value is a valid identifier
   */
  public static isIdentifier(value: unknown, maxLength: number): value is string {
    return (
      StrHlp.isNonEmptyString(value) &&
      value === value.trim() &&
      value.length <= maxLength &&
      Array.from(value).every((character) => {
        const characterCode = character.charCodeAt(0);
        return characterCode > 0x1f && characterCode !== 0x7f;
      })
    );
  }

  /**
   * Trims and returns a bounded identifier.
   * @param {unknown} value     Value to read
   * @param {number}  maxLength Maximum identifier length
   * @return {string | null} Trimmed identifier or null
   */
  public static trimToIdentifier(value: unknown, maxLength: number): string | null {
    if (typeof value !== 'string') return null;
    const identifier = value.trim();
    return StrHlp.isIdentifier(identifier, maxLength) ? identifier : null;
  }

  /**
   * Checks if object is string type
   * @param {unknown} s object to check
   * @return {boolean} string type flag
   */
  public static isString(s: unknown): boolean {
    return typeof s === 'string' || s instanceof String;
  }

  /**
   * Join string array with given string
   * @param {any}    toJoin  array (or not) to join
   * @param {Object} options struct with options: connector and placeholder
   * @return {string} string with joined array objects,
   *                  placeholder if array is empty or simple string if given object is not array
   */
  public static join(toJoin: any, options?: { connector?: string; placeholder?: string }): string {
    const placeholder = options?.placeholder ?? '';
    if (Array.isArray(toJoin)) {
      return toJoin.length ? toJoin.join(options?.connector ?? ', ') : placeholder;
    } else {
      return ObjHlp.haveValue(toJoin) ? toJoin.toString() : placeholder;
    }
  }

  /**
   * More secure version of string.split()
   * @param {string | null | undefined} str
   * @param {string | RegExp}           separator A string that identifies character or characters to use in separating the
   *                                              string. If omitted, a single-element array containing the entire string is
   *                                              returned.
   * @param {number}                    limit     A value used to limit the number of elements returned in the array.
   * @return {string[]} empty array for null, undefined and empty string
   */
  public static split(str: string | null | undefined, separator: string | RegExp, limit?: number): string[] {
    return str ? str.split(separator, limit) : [];
  }

  /**
   * Trims string and converts empty values to null
   * @param {string | null | undefined} str
   * @return {string | null}
   */
  public static trimToNull(str: string | null | undefined): string | null {
    const trimmed = str?.trim();
    return trimmed ? trimmed : null;
  }

  /**
   * Combine strings if core exists, otherwise return empty string
   * @param {string} core
   * @param {string} prefix
   * @param {string} suffix
   * @return {string} prefix + core + suffix or empty string if core is empty or not exist
   */
  public static combineIf(core: string | null | undefined, prefix?: string, suffix?: string): string {
    return core ? (prefix ?? '') + core + (suffix ?? '') : '';
  }

  /**
   * Capitalize the word
   * @param {string}  text      text to capitalize
   * @param {boolean} restLower flag that indicates if the rest of the text should be lowercase'd
   * @return {string} Text with first letter uppercase, rest is lowercase
   */
  public static capitalize(text?: string, restLower = false): string {
    if (text) {
      const rest = text.slice(1);
      return text[0].toLocaleUpperCase() + (restLower ? rest.toLocaleLowerCase() : rest);
    }
    return '';
  }

  /** @prettierignore
   * Replaces each group of white spaces in string with one single space
   * @param {string} str
   * @param {string} replacer
   * @return {string}
   * @example "Ala\nma\r\n\t\f\vkota" => "Ala ma kota"
   */
  public static mergeWhitespace(str: string, replacer = ' '): string {
    return str.replace(/\s+/g, replacer);
  }

  /**
   * Shorts string to given length and adds "..." on cuts
   * @param {string} text
   * @param {string} length
   * @param {number} start
   * @return {string}
   */
  public static shorten(text: string, length: number, start = 0): string {
    const substring = text.substring(start, start + length);
    return (start > 0 ? '...' : '') + substring + (substring.length < length ? '' : '...');
  }

  /**
   * Returns index of first different character of given strings, or -1 if strings are equal
   * @param {string} a
   * @param {string} b
   * @return {number}
   */
  public static firstDiff(a: string, b: string): number {
    const length = Math.min(a.length, b.length);
    for (let i = 0; i < length; i++) {
      if (a[i] != b[i]) return i;
    }
    return a.length == b.length ? -1 : length;
  }

  /**
   * Insert string into string
   * @param {string} str
   * @param {number} index insertion index
   * @param {string} str2  value to insert
   * @return {string}
   */
  public static insert(str: string, index: number, str2: string): string;

  /**
   * Insert characters from given codes into string
   * @param {string}   str
   * @param {number}   index insertion index
   * @param {number[]} codes value to insert as char codes
   * @return {string}
   */
  public static insert(str: string, index: number, ...codes: number[]): string;

  // implementation
  // eslint-disable-next-line require-jsdoc
  public static insert(str: string, index: number, value: string | number | number[]): string {
    return StrHlp._replaceBase(str, index, index, value);
  }

  /**
   * Replace substring
   * @param {string} str
   * @param {number} start  of replaced substring
   * @param {number} length of replaced substring
   * @param {string} str2   value to insert
   * @return {string}
   */
  public static replace(str: string, start: number, length: number, str2: string): string;

  /**
   * Replace substring using char codes
   * @param {string}   str
   * @param {number}   start  of replaced substring
   * @param {number}   length of replaced substring
   * @param {number[]} codes  value to insert as char codes
   * @return {string}
   */
  public static replace(str: string, start: number, length: number, ...codes: number[]): string;

  // implementation
  // eslint-disable-next-line require-jsdoc
  public static replace(str: string, start: number, length: number, value: string | number | number[]): string {
    return StrHlp._replaceBase(str, start, start + length, value);
  }

  /**
   * Replace single character
   * @param {string} str
   * @param {number} index of replaced char
   * @param {string} str2  value to insert
   * @return {string}
   */
  public static replaceAt(str: string, index: number, str2: string): string;

  /**
   * Replace single character using char codes
   * @param {string}   str
   * @param {number}   index of replaced char
   * @param {number[]} codes value to insert as char codes
   * @return {string}
   */
  public static replaceAt(str: string, index: number, ...codes: number[]): string;

  // implementation
  // eslint-disable-next-line require-jsdoc
  public static replaceAt(str: string, index: number, value: string | number | number[]): string {
    return StrHlp._replaceBase(str, index, index + 1, value);
  }

  // common implementation of insert(), replace() and replaceAt()
  // eslint-disable-next-line require-jsdoc
  private static _replaceBase(str: string, start: number, end: number, value: string | number | number[]): string {
    if (typeof value == 'number') value = String.fromCharCode(value);
    if (Array.isArray(value)) value = String.fromCharCode(...value);
    return str.substring(0, start) + value + str.substring(end);
  }

  /**
   * Checks if string includes ANY value in array
   * @param {string}   str    string to check
   * @param {string[]} values array of strings to check
   * @return {boolean}
   */
  public static includesAny(str: string, values: string[]): boolean {
    for (const value of values) {
      if (str.includes(value)) return true;
    }
    return false;
  }

  /**
   * Replace each $tag with value given in replaceMap
   * @param {string}     str
   * @param {ReplaceMap} map
   * @return {string}
   */
  public static replaceTags(str: string, map: ReplaceMap): string {
    let ret = str;
    for (const tag in map) {
      const value = map[tag as keyof ReplaceMap];
      if (value != null) {
        // ? tags starts with '$' so we must escape this char to build valid regexp
        const regexp = new RegExp('\\' + tag, 'g');
        ret = ret.replace(regexp, value.toString());
      }
    }
    return ret;
  }

  /**
   * Checks if string includes value after parsing them both with given parser function
   * @param {string}   str    string to check
   * @param {string}   search string to search for
   * @param {function} parser function to parse strings before checking
   * @return {boolean}
   */
  public static includesParsed(str: string, search: string, parser: (x: string) => string = (x) => x.toLowerCase()): boolean {
    return parser(str).includes(parser(search));
  }

  /**
   * Replaces all diacritic characters by their base letters
   * @param {string} str
   * @return {string} cleared string
   */
  public static clearDiacritics(str: string): string {
    // handle composed characters
    for (const x of diacriticRegex) {
      str = str.replace(new RegExp(x.letters, 'g'), x.base);
    }
    // handle decomposed characters
    str = str.replace(/[\u0300-\u036f]/g, '');
    return str;
  }

  private static GSM7Regex =
    // eslint-disable-next-line max-len, no-useless-escape
    /^[A-Za-z0-9 \r\n@£$¥èéùìòÇØøÅå\u0394_\u03A6\u0393\u039B\u03A9\u03A0\u03A8\u03A3\u0398\u039EÆæßÉ!"#$%&'()*+,\-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\\[~\]|\u20AC]*$/g;

  // TODO description
  /**
   * @param {string} str
   * @return {string}
   */
  public static clearGSM7Chars(str: string): string {
    return str.replace(this.GSM7Regex, '');
  }
}
